import formatPeriodLabel from '../../helpers/compareHelpers/formatPeriodLabel'

// AI-CONTEXT: See docs/features/compare-two-periods.md
// Small-multiples layout for comparing two calendar periods: renders the SAME
// visualisation twice, once per period, side by side. The caller supplies a
// `renderViz(range, side)` that renders the chosen shared *Visualization with
// `hideControls` and the side's period bounds — so the ~40 leaf chart
// components stay period-unaware and the frame stays chart-type-agnostic.
//
// This is the universal baseline that works for every graph type. Two graph
// types get a nicer merged view via SEPARATE sibling components the caller
// picks instead of this frame: KpiDeltaCompare (KPI numbers) and
// GroupedBarCompare (single-moment bar charts). See the spec's T4 deviation.
//
// Each side fetches independently (its own useHierarchyData call inside its
// viz), so one side's empty/loading/error state is naturally isolated from the
// other — no per-side handling needed here.

/**
 * @typedef {{start: string|null, end: string|null}|null} PeriodRange
 */

/**
 * @param {object} props
 * @param {PeriodRange} props.rangeA - Primary period (null = all-time)
 * @param {PeriodRange} props.rangeB - Compare period (null = all-time)
 * @param {(range: PeriodRange, side: 'A'|'B') => React.ReactNode} props.renderViz - Renders the chart-only viz for one period
 * @param {string} [props.labelA] - Header label for the primary period (defaults to the formatted range)
 * @param {string} [props.labelB] - Header label for the compare period (defaults to the formatted range)
 * @param {React.ReactNode} [props.toolbar] - Optional full-width row above both panels — e.g. a shared item navigator that drives which item both sides show (multi-item cards)
 * @returns {JSX.Element}
 */
const ComparisonFrame = ({ rangeA, rangeB, renderViz, labelA, labelB, toolbar }) => {
    const headerA = labelA || formatPeriodLabel(rangeA)
    const headerB = labelB || formatPeriodLabel(rangeB)

    return (
        <div className='comparison-frame-wrap'>
            {toolbar && <div className='comparison-frame-toolbar'>{toolbar}</div>}
            <div className='comparison-frame'>
            <div className='comparison-frame-panel'>
                <div className='comparison-frame-panel-header comparison-frame-panel-header--a'>
                    <span className='comparison-frame-swatch' />
                    <span className='comparison-frame-panel-label'>{headerA}</span>
                </div>
                <div className='comparison-frame-panel-body'>
                    {renderViz(rangeA, 'A')}
                </div>
            </div>
            <div className='comparison-frame-panel'>
                <div className='comparison-frame-panel-header comparison-frame-panel-header--b'>
                    <span className='comparison-frame-swatch' />
                    <span className='comparison-frame-panel-label'>{headerB}</span>
                </div>
                <div className='comparison-frame-panel-body'>
                    {renderViz(rangeB, 'B')}
                </div>
            </div>
            </div>
        </div>
    )
}

export default ComparisonFrame
